import { createFileRoute } from "@tanstack/react-router"
import { IconFileText, IconScale, IconArrowRight, IconGavel, IconShieldOff } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import {
  Card,
  Button,
  Text,
  Center,
  Box,
  useMantineColorScheme,
  ActionIcon,
  Title,
  Stack,
  Grid,
  Container,
  Group,
  List,
} from "@mantine/core"
import { SEO } from "@/components/seo/SEO"
import { ShareButtons } from "@/components/ui/ShareButtons"
import { StructuredData, createFAQPageSchema, type FAQ } from "@/components/seo/StructuredData"
import { FAQSection } from "@/components/ui/FAQSection"
import { PageCTASection } from "@/components/ui/PageCTASection"

export const Route = createFileRoute("/reform")({
  component: ReformLandingPage,
})

const reformFAQs: FAQ[] = [
  {
    question: "What systemic reforms are needed?",
    answer:
      "The Epstein case exposes critical failures requiring systemic reform: (1) Mandatory transparency in high-profile plea deals—no secret agreements that hide crimes and accomplices, (2) Equal prosecution standards regardless of wealth or connections—end two-tiered justice, (3) Institutional accountability for organizations and agencies that enabled abuse through negligence or cover-ups, (4) Public disclosure requirements for cases involving child exploitation—transparency protects future victims, and (5) Independent oversight of prosecutorial discretion in cases involving powerful individuals to prevent corrupt bargains.",
  },
  {
    question: "How does the Epstein case demonstrate two-tiered justice?",
    answer:
      "Jeffrey Epstein received an unprecedented 'sweetheart deal' in 2008 that concealed his crimes and protected co-conspirators. He served only 13 months with work release privileges, while victims were denied justice and unknown accomplices escaped accountability entirely. This stands in stark contrast to how ordinary citizens are prosecuted for similar crimes. Recent government 'releases' containing 97% previously public material show this two-tiered system continues—powerful individuals receive protection while the public receives manipulation. Equal justice demands equal treatment under law.",
  },
  {
    question: "Why is transparency crucial for justice reform?",
    answer:
      "Transparency is the foundation of accountability and reform. Secret plea deals, sealed documents, and classified information allow powerful individuals to escape consequences while hiding systemic corruption. The Epstein case shows how secrecy enables abuse: his 2008 deal was negotiated in secret, victims were kept in the dark, and accomplices were never identified or charged. Complete transparency exposes patterns of corruption, holds institutions accountable, and allows lawmakers and citizens to implement meaningful reforms. Without transparency, reform is impossible.",
  },
  {
    question: "What legislative changes would prevent future Epstein-style deals?",
    answer:
      "Key legislative reforms include: (1) Requiring public disclosure of all plea agreements in federal cases, with narrow exceptions for national security, (2) Mandatory victim notification and participation rights in plea negotiations, (3) Independent review panels for prosecutorial decisions in cases involving high-profile or politically connected individuals, (4) Prohibition of non-prosecution agreements that shield unnamed co-conspirators from investigation, and (5) Automatic review mechanisms when sentences deviate significantly from sentencing guidelines. These reforms ensure sunlight on the justice process and prevent sweetheart deals for the elite.",
  },
  {
    question: "How can individuals support justice reform?",
    answer:
      "You can drive systemic change by: (1) Demanding your representatives support transparency legislation and equal justice reforms, (2) Voting for candidates committed to ending two-tiered justice, regardless of party, (3) Supporting organizations working for criminal justice reform and transparency, (4) Sharing information about cases like Epstein's to educate others about systemic problems, and (5) Holding prosecutors and elected officials accountable when they enable preferential treatment for the powerful. Use the action page to contact your representatives directly about Epstein file releases and justice reform.",
  },
]

function ReformLandingPage() {
  const { colorScheme } = useMantineColorScheme()

  return (
    <>
      <SEO
        title="Equal Justice Under Law - No Exceptions"
        description="The Epstein case exposes how wealth and power shield criminals from accountability. Demand systemic reform and equal justice for all, regardless of status."
        canonical="https://rtefn.com/reform"
        keywords="justice reform, two-tiered justice, equal treatment under law, systemic reform, criminal justice reform, ending preferential treatment, accountability for the powerful, wealth and justice, legal system reform, fair prosecution"
        ogType="website"
        ogImage="https://rtefn.com/og-reform.png"
        ogImageAlt="Equal Justice Under Law - Release The Epstein Files Now"
      />
      <StructuredData data={createFAQPageSchema(reformFAQs)} />
      <Box className="animate-fade-in">
        {/* Hero Section */}
        <Box
          className={`min-h-screen flex items-center justify-center relative overflow-hidden py-16 ${
            colorScheme === "dark" ? "hero-dark" : "bg-gradient-to-br from-slate-50 via-white to-indigo-50"
          }`}
        >
          {colorScheme === "dark" && <Box className="noise-overlay" />}

          <Container size="xl" className="relative z-10">
            <Stack ta="center" maw={1280} mx="auto">
              {/* Tagline */}
              <Box className="animate-fade-in-up" style={{ animationDelay: "0.2s", marginBottom: "var(--space-3xl)" }}>
                <Box
                  component="span"
                  className="inline-block backdrop-blur-md text-lg font-medium rounded-full"
                  style={{
                    letterSpacing: "0.025em",
                    padding: "12px 28px",
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(79, 70, 229, 0.08)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(79, 70, 229, 0.2)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(67, 56, 202, 1)",
                  }}
                >
                  Equal Justice • Systemic Reform • True Accountability
                </Box>
              </Box>

              {/* Main Headline */}
              <h1
                className="animate-fade-in-up"
                style={{
                  animationDelay: "0.4s",
                  fontFamily: "var(--font-family)",
                  fontWeight: 700,
                  letterSpacing: "-0.04em",
                  lineHeight: 0.95,
                  fontSize: "clamp(3rem, 8vw, 7.5rem)",
                  margin: "0 0 var(--space-2xl) 0",
                  color: colorScheme === "dark" ? "#ffffff" : "#1a1a1a",
                }}
              >
                Equal Justice Under Law
                <br />
                <span className="gradient-text">No Exceptions</span>
              </h1>

              {/* Subtitle */}
              <div className="animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
                <p
                  className="max-w-4xl mx-auto"
                  style={{
                    fontFamily: "var(--font-family)",
                    fontWeight: 400,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.5,
                    fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                    color: colorScheme === "dark" ? "rgba(255, 255, 255, 0.8)" : "#6b7280",
                    margin: "0 0 var(--space-3xl) 0",
                  }}
                >
                  When the powerful escape justice, we all lose. This case demands systemic reform to ensure equal
                  treatment under law.
                </p>
              </div>

              {/* CTA Buttons */}
              <Box className="animate-fade-in-up" mt="xl" mb="3xl" style={{ animationDelay: "0.8s" }}>
                <PageCTASection
                  contextualAction={{ href: "/documents", label: "Review Evidence", icon: IconFileText }}
                />
              </Box>
            </Stack>
          </Container>
        </Box>

        {/* The Two-Tiered Justice Problem */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0a0a0a 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #eef2ff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Box maw={1152} mx="auto">
              <Card
                shadow="lg"
                radius="xl"
                p="3xl"
                style={{
                  background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  border:
                    colorScheme === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(79, 70, 229, 0.2)",
                }}
              >
                <Stack gap="xl">
                  <Group justify="center" gap="md" align="center">
                    <ActionIcon
                      variant="subtle"
                      size={64}
                      radius="md"
                      style={{ color: colorScheme === "dark" ? "#818cf8" : "#4f46e5" }}
                    >
                      <IconScale size={40} />
                    </ActionIcon>
                    <Title order={2} size="3rem" fw={700}>
                      The <span style={{ color: colorScheme === "dark" ? "#818cf8" : "#4f46e5" }}>Broken System</span>
                    </Title>
                  </Group>

                  <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center">
                    The Jeffrey Epstein case is a stark example of a <strong>two-tiered justice system</strong> where
                    wealth, power, and connections shield criminals from accountability. While ordinary Americans face
                    harsh penalties for far lesser crimes, powerful elites received sweetheart deals, delayed
                    prosecutions, and protections that would never be available to the average citizen. This is not
                    justice—it's corruption disguised as law enforcement.
                  </Text>

                  <Box mt="lg">
                    <Stack gap="md">
                      <Text size="lg" fw={600} ta="center" c="dimmed">
                        Systemic Failures That Must Be Addressed:
                      </Text>
                      <List spacing="md" size="md">
                        <List.Item>
                          <strong>Preferential Treatment:</strong> The 2008 "sweetheart deal" gave Epstein minimal
                          punishment despite overwhelming evidence
                        </List.Item>
                        <List.Item>
                          <strong>Lack of Transparency:</strong> Government agencies refuse to release full documents,
                          claiming "privacy" while protecting powerful individuals
                        </List.Item>
                        <List.Item>
                          <strong>Inadequate Prosecution:</strong> Prosecutors prioritized protecting Epstein's
                          associates over justice for victims
                        </List.Item>
                        <List.Item>
                          <strong>Institutional Complicity:</strong> Multiple agencies and officials knew about abuse
                          but failed to act
                        </List.Item>
                        <List.Item>
                          <strong>Victim Erasure:</strong> Survivors' voices were suppressed while powerful men's
                          reputations were protected
                        </List.Item>
                      </List>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Box>
          </Container>
        </Box>

        {/* Reforms Needed */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)"
                : "linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="xl">
            <Stack ta="center" mb="4xl">
              <Title order={2} size="3rem" fw={700} mb="xl" ta="center">
                Reforms <span style={{ color: colorScheme === "dark" ? "#818cf8" : "#4f46e5" }}>We Must Demand</span>
              </Title>
              <Text size="xl" lh={1.6} maw={900} mx="auto" ta="center" c="dimmed" mb="xl">
                Preventing future failures requires fundamental changes to how justice is administered
              </Text>
            </Stack>

            <Grid gutter="xl">
              {/* Transparency Requirements */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(79, 70, 229, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#4f46e5" : "#818cf8",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconFileText size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Mandatory Transparency
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      <strong>Require full disclosure</strong> of plea deals, settlements, and prosecutorial decisions
                      in cases involving public figures. No more secret agreements that shield the powerful while
                      claiming "privacy."
                    </Text>

                    <Button
                      component={Link}
                      to="/documents"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(79, 70, 229, 0.15)" : "rgba(79, 70, 229, 0.08)",
                        color: colorScheme === "dark" ? "#a5b4fc" : "#4f46e5",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(79, 70, 229, 0.3)" : "rgba(79, 70, 229, 0.2)"}`,
                      }}
                    >
                      See Current Docs
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Prosecution Standards */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(79, 70, 229, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#4f46e5" : "#818cf8",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconGavel size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Equal Prosecution
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      <strong>Eliminate preferential treatment</strong> in prosecutorial decisions. The same evidence
                      standards and sentencing guidelines must apply regardless of wealth, status, or political
                      connections.
                    </Text>

                    <Button
                      component={Link}
                      to="/timeline"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(79, 70, 229, 0.15)" : "rgba(79, 70, 229, 0.08)",
                        color: colorScheme === "dark" ? "#a5b4fc" : "#4f46e5",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(79, 70, 229, 0.3)" : "rgba(79, 70, 229, 0.2)"}`,
                      }}
                    >
                      See Timeline
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Accountability Mechanisms */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card
                  shadow="lg"
                  radius="xl"
                  p="2xl"
                  h="100%"
                  className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{
                    background: colorScheme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(20px)",
                    border:
                      colorScheme === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(79, 70, 229, 0.2)",
                  }}
                >
                  <Stack gap="md" h="100%">
                    <Center>
                      <Box
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" ? "#4f46e5" : "#818cf8",
                          borderRadius: "var(--mantine-radius-md)",
                          boxShadow: "var(--mantine-shadow-lg)",
                        }}
                      >
                        <IconShieldOff size={32} style={{ color: "white" }} />
                      </Box>
                    </Center>

                    <Text size="2xl" fw={600} ta="center">
                      Institutional Accountability
                    </Text>

                    <Text size="base" ta="center" c="dimmed" lh={1.6} style={{ flex: 1 }}>
                      <strong>Hold officials accountable</strong> when they fail to prosecute, hide evidence, or give
                      preferential treatment. Prosecutors and agents who protect the powerful must face consequences.
                    </Text>

                    <Button
                      component={Link}
                      to="/news"
                      variant="light"
                      fullWidth
                      rightSection={<IconArrowRight size={16} />}
                      style={{
                        background: colorScheme === "dark" ? "rgba(79, 70, 229, 0.15)" : "rgba(79, 70, 229, 0.08)",
                        color: colorScheme === "dark" ? "#a5b4fc" : "#4f46e5",
                        border: `1px solid ${colorScheme === "dark" ? "rgba(79, 70, 229, 0.3)" : "rgba(79, 70, 229, 0.2)"}`,
                      }}
                    >
                      Latest Updates
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>
          </Container>
        </Box>

        {/* Bipartisan Issue */}
        <Box
          style={{
            background:
              colorScheme === "dark"
                ? "linear-gradient(180deg, #1a1a2e 0%, #0f0f23 100%)"
                : "linear-gradient(180deg, #e0e7ff 0%, #ffffff 100%)",
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
          }}
        >
          <Container size="lg">
            <Card
              shadow="md"
              radius="lg"
              p="xl"
              style={{
                background: colorScheme === "dark" ? "rgba(79, 70, 229, 0.1)" : "rgba(79, 70, 229, 0.05)",
                border: `2px solid ${colorScheme === "dark" ? "rgba(79, 70, 229, 0.3)" : "rgba(79, 70, 229, 0.2)"}`,
              }}
            >
              <Stack gap="md">
                <Group>
                  <IconScale size={24} style={{ color: colorScheme === "dark" ? "#818cf8" : "#4f46e5" }} />
                  <Text size="lg" fw={600}>
                    This Is Not a Partisan Issue
                  </Text>
                </Group>
                <Text size="md" lh={1.6} c="dimmed">
                  Equal justice should unite Americans across the political spectrum. Whether you lean left or right,
                  libertarian or progressive, we all have a stake in ensuring that wealth and power do not place anyone
                  above the law.
                </Text>
                <Text size="md" lh={1.6} c="dimmed">
                  The Epstein case implicates individuals across party lines, industries, and institutions. True reform
                  means holding <strong>everyone</strong> accountable—regardless of political affiliation, social
                  status, or financial resources. This is about fundamental fairness and the integrity of our justice
                  system.
                </Text>
              </Stack>
            </Card>
          </Container>
        </Box>

        {/* FAQ Section */}
        <FAQSection
          faqs={reformFAQs}
          accentColor={colorScheme === "dark" ? "#818cf8" : "#4f46e5"}
          title="Frequently Asked Questions"
          subtitle="Answers to common questions about justice reform and systemic change"
        />

        {/* Call to Action */}
        <Box
          ta="center"
          style={{
            paddingTop: "var(--section-spacing-lg)",
            paddingBottom: "var(--section-spacing-lg)",
            backgroundColor: colorScheme === "dark" ? "#030712" : "#3730a3",
          }}
        >
          <Container size="xl">
            <Stack maw={896} mx="auto" ta="center">
              <Title order={2} size="3rem" fw={600} mb="md" c="white">
                Demand Systemic Reform
              </Title>

              <Text size="xl" c="gray.3" mb="xl" maw={768} mx="auto" fw={300} lh={1.6}>
                Justice delayed is justice denied. We cannot wait for another scandal to expose these systemic failures.
                The time for reform is now.
              </Text>

              <Stack hiddenFrom="sm" gap="lg">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Demand Equal Justice
                </Link>
              </Stack>
              <Group visibleFrom="sm" gap="lg" justify="center">
                <Link
                  to="/action"
                  className="apple-button apple-button-primary text-lg px-8 py-4 min-w-[180px] font-medium bg-white text-gray-900 hover:bg-gray-100"
                >
                  Demand Equal Justice
                </Link>
              </Group>

              {/* Share Section */}
              <Box mt="2xl">
                <Text size="sm" c="gray.4" mb="sm" ta="center" fw={500}>
                  Join the movement for equal justice
                </Text>
                <Group justify="center">
                  <ShareButtons size="lg" variant="filled" />
                </Group>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Box>
    </>
  )
}
